import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, MapPin, Mail, Phone, Quote, Coffee, Film, Cookie, Heart, Globe, Instagram, Youtube, Edit, Upload, X } from "lucide-react";
import { SiTiktok, SiLinkedin, SiFacebook, SiYoutube } from "react-icons/si";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getAvatarSource, getAvatarInitials } from "@/lib/avatar";
import { useToast } from "@/hooks/use-toast";
import { useProfilePictureUpload } from "@/hooks/use-profile-picture-upload";
import { SquareCropDialog } from "@/components/square-crop-dialog";

interface ProfileEntry {
  id: string;
  type: "personal" | "talent";
  firstName?: string;
  lastName?: string;
  displayName: string;
  title: string | null;
  bioShort: string | null;
  photoUrl: string | null;
  role: string;
  isPublished: boolean;
  hasProfile?: boolean;
  linkedUserId?: string;
}

interface PersonalProfileDetail {
  id: string;
  userId: string;
  title: string | null;
  location: string | null;
  bioShort: string | null;
  bioLong: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  socialLinks: string | null;
  hobbies: string | null;
  favoriteQuote: string | null;
  favoriteMovieShow: string | null;
  coffeeOrder: string | null;
  favoriteSnack: string | null;
  isPublished: boolean;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  profileImageUrl?: string;
  profilePicture?: string;
}

interface ProfileDetailViewProps {
  profile: ProfileEntry;
  isAdmin: boolean;
  currentUserId: string;
  onClose: () => void;
}

export default function ProfileDetailView({ profile, isAdmin, currentUserId, onClose }: ProfileDetailViewProps) {
  const isPersonal = profile.type === "personal";
  const { toast } = useToast();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    location: "",
    bioShort: "",
    bioLong: "",
    contactEmail: "",
    contactPhone: "",
    hobbies: "",
    favoriteQuote: "",
    favoriteMovieShow: "",
    coffeeOrder: "",
    favoriteSnack: "",
    isPublished: false,
    profilePicture: "",
  });

  const {
    pickFile: pickAdminPhoto,
    fileInputProps: adminFileInputProps,
    selectedFile: adminSelectedFile,
    cropOpen: adminCropOpen,
    cancelCrop: cancelAdminCrop,
    confirmCrop: confirmAdminCrop,
    isUploading,
  } = useProfilePictureUpload({
    onUploaded: (objectPath) => {
      setEditForm((prev) => ({ ...prev, profilePicture: objectPath }));
    },
  });

  const { data: fullProfile, isLoading, refetch } = useQuery<PersonalProfileDetail>({
    queryKey: ["/api/profiles/personal", profile.id],
    enabled: isPersonal,
  });

  const { data: talent, isLoading: talentLoading } = useQuery({
    queryKey: ["/api/talent", profile.id],
    enabled: !isPersonal,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof editForm) => {
      return apiRequest("PATCH", `/api/profiles/admin/${profile.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profiles/personal", profile.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/profiles/directory"] });
      toast({
        title: "Profile updated",
        description: "The profile has been saved successfully.",
      });
      setShowEditDialog(false);
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  const handleEditClick = () => {
    if (fullProfile) {
      setEditForm({
        title: fullProfile.title || "",
        location: fullProfile.location || "",
        bioShort: fullProfile.bioShort || "",
        bioLong: fullProfile.bioLong || "",
        contactEmail: fullProfile.contactEmail || "",
        contactPhone: fullProfile.contactPhone || "",
        hobbies: fullProfile.hobbies || "",
        favoriteQuote: fullProfile.favoriteQuote || "",
        favoriteMovieShow: fullProfile.favoriteMovieShow || "",
        coffeeOrder: fullProfile.coffeeOrder || "",
        favoriteSnack: fullProfile.favoriteSnack || "",
        isPublished: fullProfile.isPublished || false,
        profilePicture: (fullProfile as any).profilePicture || "",
      });
    }
    setShowEditDialog(true);
  };


  const handleSave = () => {
    updateMutation.mutate(editForm);
  };

  const getInitials = (name: string) => getAvatarInitials(name);

  const parseSocialLinks = (socialLinksStr: string | null): Record<string, string> => {
    if (!socialLinksStr) return {};
    try {
      return JSON.parse(socialLinksStr);
    } catch {
      return {};
    }
  };

  if ((isPersonal && isLoading) || (!isPersonal && talentLoading)) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isPersonal && fullProfile) {
    const socialLinks = parseSocialLinks(fullProfile.socialLinks);
    const hasContactInfo = fullProfile.contactEmail || fullProfile.contactPhone;
    const hasSocialLinks = Object.keys(socialLinks).length > 0;
    const hasFunFacts =
      fullProfile.hobbies ||
      fullProfile.favoriteQuote ||
      fullProfile.favoriteMovieShow ||
      fullProfile.coffeeOrder ||
      fullProfile.favoriteSnack;

    return (
      <>
      <div className="space-y-6">
        <div className="flex items-start gap-6">
          <Avatar className="h-24 w-24 border-2 border-border">
            <AvatarImage
              src={getAvatarSource({
                profilePicture: fullProfile.profilePicture,
                profileImageUrl: fullProfile.profileImageUrl,
              })}
              alt={fullProfile.displayName || profile.displayName}
            />
            <AvatarFallback className="text-2xl bg-muted">
              {getInitials(fullProfile.displayName || profile.displayName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-2xl font-bold">{fullProfile.displayName || profile.displayName}</h2>
                {fullProfile.title && (
                  <p className="text-lg text-muted-foreground">{fullProfile.title}</p>
                )}
              </div>
              {isAdmin && isPersonal && (
                <Button variant="outline" size="sm" onClick={handleEditClick} data-testid="button-admin-edit-profile">
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            {fullProfile.location && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <MapPin className="h-4 w-4" />
                {fullProfile.location}
              </div>
            )}
            <Badge variant="secondary" className="mt-2">
              {profile.role === "sales_rep" ? "Sales" : profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
            </Badge>
          </div>
        </div>

        {fullProfile.bioLong && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-2">About</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{fullProfile.bioLong}</p>
            </div>
          </>
        )}

        {hasContactInfo && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Contact</h3>
              <div className="flex flex-wrap gap-4">
                {fullProfile.contactEmail && (
                  <a
                    href={`mailto:${fullProfile.contactEmail}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    {fullProfile.contactEmail}
                  </a>
                )}
                {fullProfile.contactPhone && (
                  <a
                    href={`tel:${fullProfile.contactPhone}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {fullProfile.contactPhone}
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {hasSocialLinks && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Connect</h3>
              <div className="flex flex-wrap gap-3">
                {socialLinks.website && (
                  <a
                    href={socialLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="text-sm">Website</span>
                  </a>
                )}
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    <span className="text-sm">Instagram</span>
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a
                    href={socialLinks.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <SiTiktok className="h-4 w-4" />
                    <span className="text-sm">TikTok</span>
                  </a>
                )}
                {socialLinks.youtube && (
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <Youtube className="h-4 w-4" />
                    <span className="text-sm">YouTube</span>
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <SiLinkedin className="h-4 w-4" />
                    <span className="text-sm">LinkedIn</span>
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {hasFunFacts && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Fun Facts</h3>
              <div className="grid gap-3">
                {fullProfile.hobbies && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Heart className="h-5 w-5 text-pink-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Hobbies</p>
                      <p className="text-sm text-muted-foreground">{fullProfile.hobbies}</p>
                    </div>
                  </div>
                )}
                {fullProfile.favoriteQuote && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Quote className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Favorite Quote</p>
                      <p className="text-sm text-muted-foreground italic">"{fullProfile.favoriteQuote}"</p>
                    </div>
                  </div>
                )}
                {fullProfile.favoriteMovieShow && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Film className="h-5 w-5 text-purple-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Favorite Movie/Show</p>
                      <p className="text-sm text-muted-foreground">{fullProfile.favoriteMovieShow}</p>
                    </div>
                  </div>
                )}
                {fullProfile.coffeeOrder && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Coffee className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Coffee Order</p>
                      <p className="text-sm text-muted-foreground">{fullProfile.coffeeOrder}</p>
                    </div>
                  </div>
                )}
                {fullProfile.favoriteSnack && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Cookie className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Favorite Snack</p>
                      <p className="text-sm text-muted-foreground">{fullProfile.favoriteSnack}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Profile - {fullProfile.displayName || profile.displayName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Profile Photo</h3>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <Avatar className="h-20 w-20 border-2 border-border">
                    <AvatarImage
                      src={
                        editForm.profilePicture ||
                        getAvatarSource({
                          profilePicture: fullProfile.profilePicture,
                          profileImageUrl: fullProfile.profileImageUrl,
                        })
                      }
                      alt={fullProfile.displayName || profile.displayName}
                    />
                    <AvatarFallback className="text-xl bg-muted">
                      {getInitials(fullProfile.displayName || profile.displayName)}
                    </AvatarFallback>
                  </Avatar>
                  {isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-full">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <input
                    {...adminFileInputProps}
                    data-testid="input-admin-profile-picture"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={pickAdminPhoto}
                    disabled={isUploading}
                    data-testid="button-admin-upload-photo"
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Upload Photo
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Title</Label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Senior Sales Rep"
                    data-testid="input-admin-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={editForm.location}
                    onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., Austin, TX"
                    data-testid="input-admin-location"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Short Bio</Label>
                <Input
                  value={editForm.bioShort}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bioShort: e.target.value }))}
                  placeholder="Brief tagline"
                  maxLength={255}
                  data-testid="input-admin-bio-short"
                />
              </div>

              <div className="space-y-2">
                <Label>Full Bio</Label>
                <Textarea
                  value={editForm.bioLong}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bioLong: e.target.value }))}
                  placeholder="Detailed about section"
                  rows={3}
                  data-testid="textarea-admin-bio-long"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={editForm.contactEmail}
                    onChange={(e) => setEditForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                    placeholder="email@example.com"
                    data-testid="input-admin-contact-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={editForm.contactPhone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    data-testid="input-admin-contact-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Hobbies</Label>
                <Input
                  value={editForm.hobbies}
                  onChange={(e) => setEditForm(prev => ({ ...prev, hobbies: e.target.value }))}
                  placeholder="Hobbies and interests"
                  data-testid="input-admin-hobbies"
                />
              </div>

              <div className="space-y-2">
                <Label>Favorite Quote</Label>
                <Input
                  value={editForm.favoriteQuote}
                  onChange={(e) => setEditForm(prev => ({ ...prev, favoriteQuote: e.target.value }))}
                  placeholder="An inspiring quote"
                  data-testid="input-admin-quote"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Favorite Movie/Show</Label>
                  <Input
                    value={editForm.favoriteMovieShow}
                    onChange={(e) => setEditForm(prev => ({ ...prev, favoriteMovieShow: e.target.value }))}
                    placeholder="Movie or show"
                    data-testid="input-admin-movie"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Coffee Order</Label>
                  <Input
                    value={editForm.coffeeOrder}
                    onChange={(e) => setEditForm(prev => ({ ...prev, coffeeOrder: e.target.value }))}
                    placeholder="Your go-to coffee"
                    data-testid="input-admin-coffee"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Favorite Snack</Label>
                  <Input
                    value={editForm.favoriteSnack}
                    onChange={(e) => setEditForm(prev => ({ ...prev, favoriteSnack: e.target.value }))}
                    placeholder="Favorite snack"
                    data-testid="input-admin-snack"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Switch
                  id="published"
                  checked={editForm.isPublished}
                  onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, isPublished: checked }))}
                  data-testid="switch-admin-published"
                />
                <Label htmlFor="published" className="cursor-pointer">
                  Published - Profile is visible to all team members
                </Label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)} data-testid="button-admin-cancel">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-admin-save">
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SquareCropDialog
        open={adminCropOpen}
        file={adminSelectedFile}
        onCancel={cancelAdminCrop}
        onConfirm={confirmAdminCrop}
        isUploading={isUploading}
      />
      </>
    );
  }

  if (!isPersonal && talent) {
    const ent = talent as any;
    return (
      <div className="space-y-6">
        <div className="flex items-start gap-6">
          <Avatar className="h-24 w-24 border-2 border-border">
            <AvatarImage src={getAvatarSource(ent)} alt={ent.name} />
            <AvatarFallback className="text-2xl bg-muted">
              {getInitials(ent.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{ent.name}</h2>
            <Badge variant="secondary" className="mt-2">Artist</Badge>
          </div>
        </div>

        {ent.bio && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-2">About</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{ent.bio}</p>
            </div>
          </>
        )}

        {ent.eventDescription && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-2">The Show</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{ent.eventDescription}</p>
            </div>
          </>
        )}

        {(ent.websiteUrl || (ent.socialMediaUrls && ent.socialMediaUrls.length > 0)) && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Connect</h3>
              <div className="flex flex-wrap gap-3">
                {ent.websiteUrl && (
                  <a
                    href={ent.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="text-sm">Website</span>
                  </a>
                )}
                {ent.socialMediaUrls?.map((url: string, idx: number) => {
                  const isInstagram = url.includes("instagram");
                  const isTiktok = url.includes("tiktok");
                  const isYoutube = url.includes("youtube") || url.includes("youtu.be");
                  const isLinkedin = url.includes("linkedin");
                  const isFacebook = url.includes("facebook") || url.includes("fb.com") || url.includes("fb.me");
                  
                  return (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    >
                      {isInstagram ? (
                        <Instagram className="h-4 w-4" />
                      ) : isTiktok ? (
                        <SiTiktok className="h-4 w-4" />
                      ) : isYoutube ? (
                        <SiYoutube className="h-4 w-4" />
                      ) : isLinkedin ? (
                        <SiLinkedin className="h-4 w-4" />
                      ) : isFacebook ? (
                        <SiFacebook className="h-4 w-4" />
                      ) : (
                        <Globe className="h-4 w-4" />
                      )}
                      <span className="text-sm">
                        {isInstagram
                          ? "Instagram"
                          : isTiktok
                          ? "TikTok"
                          : isYoutube
                          ? "YouTube"
                          : isLinkedin
                          ? "LinkedIn"
                          : isFacebook
                          ? "Facebook"
                          : "Link"}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Final fallback: the profile entry from the directory exists, but the
  // detail endpoint returned nothing usable (e.g. user hasn't filled in a
  // personal profile yet, or talent record is missing). Render the basics
  // we already have on the directory card so the modal isn't an empty
  // dead end.
  const fallbackName = profile.displayName || "Team member";
  const fallbackRoleLabel =
    profile.role === "sales_rep"
      ? "Sales"
      : profile.role
        ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
        : "Team member";

  return (
    <div className="space-y-6" data-testid="profile-detail-fallback">
      <div className="flex items-start gap-6">
        <Avatar className="h-24 w-24 border-2 border-border">
          <AvatarImage
            src={getAvatarSource({
              profilePicture: profile.photoUrl,
              profileImageUrl: profile.photoUrl,
            })}
            alt={fallbackName}
          />
          <AvatarFallback className="text-2xl bg-muted">
            {getInitials(fallbackName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold truncate" data-testid="text-fallback-name">
            {fallbackName}
          </h2>
          {profile.title && (
            <p className="text-lg text-muted-foreground truncate">{profile.title}</p>
          )}
          <Badge variant="secondary" className="mt-2" data-testid="badge-fallback-role">
            {fallbackRoleLabel}
          </Badge>
        </div>
      </div>

      {profile.bioShort && (
        <>
          <Separator />
          <p className="text-muted-foreground whitespace-pre-wrap">
            {profile.bioShort}
          </p>
        </>
      )}

      <Separator />
      <p className="text-sm text-muted-foreground">
        {isPersonal
          ? "This teammate hasn't filled out their personal profile yet."
          : "Additional artist details haven't been added yet."}
      </p>
    </div>
  );
}
